import { useRef } from 'react';
import { Camera, Mic, Paperclip, Square, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  canUseMicrophone,
  microphoneUnavailableMessage,
  useAudioRecording,
} from '../hooks/useAudioRecording';

const MAX_FILE_BYTES = 10 * 1024 * 1024;

type InboxComposerProps = {
  reply: string;
  attachment: File | null;
  canCompose: boolean;
  allowAttachments?: boolean;
  busy: boolean;
  error: string;
  onReplyChange: (value: string) => void;
  onAttachmentChange: (file: File | null) => void;
  onError: (message: string) => void;
  onSend: () => void;
};

function validateAttachment(file: File, onError: (message: string) => void): boolean {
  if (file.size > MAX_FILE_BYTES) {
    onError('El archivo no puede superar 10 MB');
    return false;
  }
  return true;
}

function formatRecordingTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function InboxComposer({
  reply,
  attachment,
  canCompose,
  allowAttachments = true,
  busy,
  error,
  onReplyChange,
  onAttachmentChange,
  onError,
  onSend,
}: InboxComposerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const clearAttachment = () => {
    onAttachmentChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const applyAttachment = (file: File | null) => {
    if (!file) {
      return;
    }
    if (!validateAttachment(file, onError)) {
      clearAttachment();
      return;
    }
    onAttachmentChange(file);
  };

  const { isRecording, recordingSeconds, cancelRecording, toggleRecording } = useAudioRecording({
    enabled: canCompose && !busy && allowAttachments,
    onRecordingComplete: applyAttachment,
    onError,
  });

  const handleMicClick = () => {
    if (!canUseMicrophone()) {
      onError(microphoneUnavailableMessage());
      return;
    }
    toggleRecording();
  };

  return (
    <div className='border-t p-3'>
      {error ? <p className='mb-2 text-sm text-destructive'>{error}</p> : null}
      {isRecording ? (
        <div className='mb-2 flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
          <span className='flex items-center gap-2'>
            <span className='inline-block h-2 w-2 animate-pulse rounded-full bg-red-500' />
            Grabando {formatRecordingTime(recordingSeconds)}
          </span>
          <div className='flex items-center gap-1'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => cancelRecording()}
              aria-label='Cancelar grabación'
            >
              <X className='h-3 w-3' />
            </Button>
            <Button
              type='button'
              size='sm'
              onClick={() => toggleRecording()}
              aria-label='Detener grabación'
            >
              <Square className='mr-1 h-3 w-3' />
              Listo
            </Button>
          </div>
        </div>
      ) : null}
      {attachment ? (
        <div className='mb-2 flex items-center justify-between rounded border px-2 py-1 text-xs'>
          <span className='truncate'>{attachment.name}</span>
          <button
            type='button'
            className='ml-2 shrink-0'
            onClick={clearAttachment}
            aria-label='Quitar adjunto'
          >
            <X className='h-3 w-3' />
          </button>
        </div>
      ) : null}
      <Textarea
        rows={3}
        maxLength={4096}
        value={reply}
        disabled={!canCompose || busy || isRecording}
        onChange={event => onReplyChange(event.target.value)}
        placeholder={attachment ? 'Leyenda opcional…' : 'Escribí tu respuesta…'}
      />
      <div className='mt-2 flex items-center justify-between gap-2'>
        <div className='flex flex-wrap items-center gap-1'>
          {allowAttachments ? (
            <>
          <input
            ref={fileInputRef}
            type='file'
            className='hidden'
            accept='image/jpeg,image/png,image/gif,image/webp,audio/mpeg,audio/wav,audio/ogg,audio/webm,audio/mp4,.pdf,.doc,.docx,.txt,.xls,.xlsx,.zip'
            onChange={event => {
              applyAttachment(event.target.files?.[0] ?? null);
              event.target.value = '';
            }}
          />
          <input
            ref={cameraInputRef}
            type='file'
            className='hidden'
            accept='image/*'
            capture='environment'
            onChange={event => {
              applyAttachment(event.target.files?.[0] ?? null);
              event.target.value = '';
            }}
          />
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={!canCompose || busy || isRecording}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className='mr-1 h-4 w-4' />
            Adjuntar
          </Button>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={!canCompose || busy || isRecording}
            onClick={() => cameraInputRef.current?.click()}
            aria-label='Tomar foto'
          >
            <Camera className='mr-1 h-4 w-4' />
            Foto
          </Button>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={!canCompose || busy || !!attachment}
            onClick={handleMicClick}
            aria-label={isRecording ? 'Detener grabación' : 'Grabar audio'}
            className={isRecording ? 'border-red-300 text-red-700' : undefined}
          >
            <Mic className='mr-1 h-4 w-4' />
            Audio
          </Button>
            </>
          ) : null}
        </div>
        <Button
          disabled={!canCompose || busy || isRecording || (!reply.trim() && !attachment)}
          onClick={onSend}
        >
          Enviar
        </Button>
      </div>
    </div>
  );
}
